// src/App.js
import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import "./App.css";

import AOS from "aos";
import "aos/dist/aos.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "react-cookie-consent";
import PageLoader from "./components/PageLoader";

// ⭐ Scroll restore
import ScrollToTop from "./ScrollToTop";

// Pages
import Homepage from "./components/Homepage";
import Register from "./components/Register";
import Login from "./components/Login";
import Termsandconditions from "./components/Termsandconditions";

import Services from "./components/Services";
import Features from "./components/Features";
import PostHospital from "./components/Refund";
import Samples from "./components/Samples";
import Aboutus from "./components/Aboutus";
import ContactUs from "./components/ContactUs";
import WhyUs from "./components/WhyUs";
import Refund from "./components/Refund";
import Order from "./components/Order";
import OrderDetails from "./components/OrderDetails";
import TaskDetails from "./components/TaskDetails";
import Specialist from "./components/Specialist";

// Academic Tools
import GrammarChecker from "./components/academictools/GrammarChecker";
import Paraphrase from "./components/academictools/Paraphrase";
import FactoringCalculator from "./components/academictools/FactoringCalculator";
import EquationSolver from "./components/academictools/EquationSolver";

// ⭐ New blog articles
import ResearchTips from "./components/blog/ResearchTips";
import ResearchEfficiently from "./components/blog/ResearchEfficiently";

// Blog article components
import AcademicAssignmentStructureGuide from "./components/blog/AcademicAssignmentStructureGuide";
import Mentalhealthguide from "./components/blog/Mentalhealthguide";
import MistakeAvoidAssignment from "./components/blog/MistakeAvoidAssignment";
import TimeManagementAssignment from "./components/blog/TimeManagementAssignment";
import WriteWithAI from "./components/blog/WriteWithAI";
import PlagiarismFree from "./components/blog/PlagiarismFree";
import PaymentSuccess from "./components/PaymentSuccess";

import AssignmentChecklist from "./components/blog/AssignmentChecklist";
import ProofreadingAssignments from "./components/blog/ProofreadingAssignments";

function App() {
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const initialStartRef = useRef(Date.now());
  const initTimerRef = useRef(null);
  const navTimerRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: "ease-in-out" });
  }, []);

  // Initial loader
  useEffect(() => {
    const MIN_INITIAL = 1500;
    const elapsed = Date.now() - initialStartRef.current;
    const remaining = Math.max(0, MIN_INITIAL - elapsed);

    initTimerRef.current = setTimeout(() => {
      setLoading(false);
      initTimerRef.current = null;
    }, remaining);

    return () => {
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
    };
  }, []);

  // Loader on navigation
  useEffect(() => {
    if (initTimerRef.current) return;

    setLoading(true);

    const NAV_MS = 700;
    if (navTimerRef.current) clearTimeout(navTimerRef.current);

    navTimerRef.current = setTimeout(() => {
      setLoading(false);
      navTimerRef.current = null;
    }, NAV_MS);

    return () => {
      if (navTimerRef.current) {
        clearTimeout(navTimerRef.current);
        navTimerRef.current = null;
      }
    };
  }, [location.pathname]);

  return (
    <div className="app">
      <ScrollToTop />

      <PageLoader loading={loading} />

      <Navbar />

      <CookieConsent
        location="bottom"
        buttonText="I understand"
        cookieName="myAwesomeCookieName"
        style={{ background: "#2B373B" }}
        buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
        expires={150}
      >
        This website uses cookies to enhance the user experience.
      </CookieConsent>

      <main>
        <Routes>
          {/* Core pages */}
          <Route path="/" element={<Homepage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/services" element={<Services />} />
          <Route path="/aboutus" element={<Aboutus />} />
          <Route path="/contactus" element={<ContactUs />} />

          {/* Blog landing */}
          <Route path="/blog" element={<WhyUs />} />
          <Route path="/whyus" element={<Navigate to="/blog" replace />} />

          {/* Existing blog articles */}
          <Route path="/blog/academic-assignment-structure" element={<AcademicAssignmentStructureGuide />} />
          <Route path="/blog/mental-health-guide" element={<Mentalhealthguide />} />
          <Route path="/blog/mistake-avoid-assignment" element={<MistakeAvoidAssignment />} />
          <Route path="/blog/time-management-assignment" element={<TimeManagementAssignment />} />
          <Route path="/blog/write-with-ai" element={<WriteWithAI />} />
          <Route path="/blog/plagiarismfree" element={<PlagiarismFree />} />

          {/* New blog articles */}
          <Route path="/blog/jstor-research-tips" element={<ResearchTips />} />
          <Route path="/blog/research-effectively" element={<ResearchEfficiently />} />

          {/* More blog content */}
          <Route path="/blog/assignment-writing-checklist" element={<AssignmentChecklist />} />
          <Route path="/blog/proofreading-assignments" element={<ProofreadingAssignments />} />

          {/* Redirects for old links */}
          <Route path="/blog/AcademicAssignmentStructureGuide" element={<Navigate to="/blog/academic-assignment-structure" replace />} />
          <Route path="/blog/Mentalhealthguide" element={<Navigate to="/blog/mental-health-guide" replace />} />
          <Route path="/blog/MistakeAvoidAssignment" element={<Navigate to="/blog/mistake-avoid-assignment" replace />} />
          <Route path="/blog/TimeManagementAssignment" element={<Navigate to="/blog/time-management-assignment" replace />} />
          <Route path="/blog/WriteWithAI" element={<Navigate to="/blog/write-with-ai" replace />} />
          <Route path="/blog/PlagiarismFree" element={<Navigate to="/blog/plagiarismfree" replace />} />

          <Route path="/blog/AssignmentChecklist" element={<Navigate to="/blog/assignment-writing-checklist" replace />} />
          <Route path="/blog/ProofreadingAssignments" element={<Navigate to="/blog/proofreading-assignments" replace />} />

          {/* Redirects for the new blogs */}
          <Route path="/blog/JSTORResearchTips" element={<Navigate to="/blog/jstor-research-tips" replace />} />
          <Route path="/blog/ResearchEffectively" element={<Navigate to="/blog/research-effectively" replace />} />

          {/* Other pages */}
          <Route path="/refund" element={<Refund />} />
          <Route path="/order" element={<Order />} />
          <Route path="/checkout/order-details" element={<OrderDetails />} />
          <Route path="/post-hospital" element={<PostHospital />} />
          <Route path="/termsandconditions" element={<Termsandconditions />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* Academic tools */}
          <Route path="/grammar-checker" element={<GrammarChecker />} />
          <Route path="/paraphrasing-tool" element={<Paraphrase />} />
          <Route path="/factoring-calculator" element={<FactoringCalculator />} />
          <Route path="/equation-solver" element={<EquationSolver />} />

          <Route path="/checkout/task-details" element={<TaskDetails />} />
          <Route path="/specialist" element={<Specialist />} />

          <Route path="/samples" element={<Samples />} />
        </Routes>
      </main>

      {location.pathname === "/" && <Features />}

      <Footer />
    </div>
  );
}

export default App;
